import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { DemographicFilterQuery } from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import type { ApiResponse } from '@/api/http/types'
import { useSessionStore } from '@/stores/useSessionStore'
import { extractDemographicFilterCatalog } from '../lib/instrument-demographic-filter-catalog'
import { serializeDemographicFilterQuery } from '../lib/instrument-demographic-filters'
import {
  normalizeInstrumentDashboardSummary,
  type InstrumentDashboardSummaryView,
} from '../lib/instrument-dashboard-normalize'

export type InstrumentDashboardQueryResult = InstrumentDashboardSummaryView & {
  demographicFilterCatalog?: DemographicFilterQuery
}

export const POLL_MS = 30_000

function tenantKey(tenantId: string | null | undefined) {
  return tenantId ?? '_'
}

export function useInstrumentDashboard(
  instrumentId: string,
  options?: { enabled?: boolean; demographicFilters?: DemographicFilterQuery },
) {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const isAuthenticated = useSessionStore((s) => s.isAuthenticated)
  const filterParams = serializeDemographicFilterQuery(options?.demographicFilters ?? {})

  return useQuery({
    queryKey: queryKeys.instrumentDashboard.summary(
      tenantKey(tenantId),
      instrumentId,
      filterParams,
    ),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<InstrumentDashboardSummaryView>>(
        `/api/v1/instrument-dashboard/${instrumentId}`,
        { params: filterParams },
      )
      if (data.data === undefined) {
        throw new Error('Instrument dashboard response missing data payload')
      }
      const raw = data.data as InstrumentDashboardSummaryView & Record<string, unknown>
      return {
        ...normalizeInstrumentDashboardSummary(raw),
        demographicFilterCatalog: extractDemographicFilterCatalog(raw),
      }
    },
    enabled:
      (options?.enabled ?? true) && isAuthenticated && !!instrumentId,
    placeholderData: keepPreviousData,
    refetchInterval: POLL_MS,
  })
}
